/**
 * Single source of truth for currency symbol/display formatting across the
 * marketplace frontend. Replaces five independent copies of this map that
 * had drifted (CA$ in one place, bare "CAD" in the others) — see the
 * student-country-currency-profile audit and impl plan.
 *
 * Only IN/US/CA are actively supported today, but any other ISO code falls
 * back to the raw code rather than throwing, since the backend's admin CRUD
 * can add new mappings without a frontend deploy.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  CAD: 'CA$',
};

/** Canonical currency symbol. Falls back to the raw currency code if unrecognized or null. */
export function currencySymbol(currencyCode: string | null | undefined): string {
  if (!currencyCode) return '';
  return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] ?? currencyCode;
}

/**
 * Format a whole-unit amount with its currency's symbol, e.g.
 * formatCurrency(200, 'INR') -> "₹200". Matches this codebase's existing
 * convention of storing/displaying offering and goal prices in whole units
 * (not minor units/cents) — see PricingPlanRequest.regularPrice (BigDecimal)
 * and existing call sites like `₹${goal.budgetMin}`.
 *
 * The one place that genuinely stores minor units (subscription plan cents)
 * keeps dividing by 100 before calling this, unchanged.
 */
export function formatCurrency(amount: number | string, currencyCode: string | null | undefined): string {
  return `${currencySymbol(currencyCode)}${amount}`;
}

/** Format a range, e.g. formatCurrencyRange(200, 2000, 'INR') -> "₹200–₹2000". */
export function formatCurrencyRange(
  min: number | string,
  max: number | string,
  currencyCode: string | null | undefined
): string {
  return `${formatCurrency(min, currencyCode)}–${formatCurrency(max, currencyCode)}`;
}
